import { useState, ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { OrdenTrabajo } from "@/shared/interfaces";
import ImagesWithZoom from "@/shared/components/ui/Images/ImagesWithZoom";
import SubirFotosProcesoTrasladoModal from "./SubirFotosProcesoTrasladoModal";
import CompletarFotosTrasladoModal from "./CompletarFotosTrasladoModal";
import {
  eliminarFotoTraslado,
  useAvisosFotosTraslado,
  useOrdenTrabajoFotosLive,
  useRefrescarFotosTrasladoLive,
  useSetFotosTrasladoLive,
} from "./fotos-traslado.actions";
import {
  EtapaFotoTraslado,
  FOTOS_FINALIZACION_REQUERIDAS,
  FOTOS_INGRESO_REQUERIDAS,
  FOTOS_PROCESO_SLOTS,
  FotoTrasladoEliminada,
  eliminadaProcesoPorTipo,
  eliminadasFinalizacionDe,
  fotoProcesoPorTipo,
  fotosFinalizacionDe,
  itemsIngresoDe,
  resumenFotosTraslado,
  textoEliminadaPor,
  textoFotosFaltantes,
} from "./fotos-traslado.utils";

type FotoAEliminar = { etapa: EtapaFotoTraslado; foto: any; titulo: string };

/** Chip "cargadas/esperadas" con color según completitud. (Se reutiliza en FotosVisitaTab.) */
export function ChipConteo({ cargadas, esperadas }: { cargadas: number; esperadas: number }) {
  const ok = cargadas >= esperadas;
  return (
    <Chip
      size="small"
      label={`${cargadas}/${esperadas}`}
      color={ok ? "success" : cargadas > 0 ? "warning" : "default"}
      variant={ok ? "filled" : "outlined"}
      icon={ok ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : undefined}
    />
  );
}

/** Botón "Eliminar" bajo una foto (solo Activaciones). */
export function BotonEliminarFoto({ onClick }: { onClick: () => void }) {
  return (
    <Button
      size="small"
      color="error"
      startIcon={<DeleteOutlineIcon fontSize="small" />}
      onClick={onClick}
      sx={{ mt: 0.5, px: 0.5, minHeight: 32, textTransform: "none" }}
    >
      Eliminar
    </Button>
  );
}

/** Recuadro que ocupa el lugar de una foto que Activaciones eliminó. */
export function RecuadroEliminada({ marca, nombre }: { marca?: FotoTrasladoEliminada | null; nombre?: string }) {
  const detalle = textoEliminadaPor(marca);
  return (
    <>
      <Box
        sx={{
          minHeight: 110,
          borderRadius: 1.5,
          border: "1px dashed",
          borderColor: "error.main",
          bgcolor: (t) => alpha(t.palette.error.main, 0.04),
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.25,
          px: 1,
          py: 1,
        }}
      >
        <ImageNotSupportedOutlinedIcon color="error" fontSize="small" />
        <Typography variant="caption" color="error" fontWeight={700} textAlign="center">
          Eliminada por Activaciones
        </Typography>
        <Typography variant="caption" color="text.secondary" textAlign="center">
          Pendiente de volver a subir
        </Typography>
      </Box>
      {nombre && (
        <Typography variant="caption" fontWeight={600} display="block" sx={{ mt: 0.5 }}>
          {nombre}
        </Typography>
      )}
      {detalle && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: nombre ? 0 : 0.5 }}>
          Eliminada {detalle}
        </Typography>
      )}
    </>
  );
}

/**
 * Sección de fotos con el MISMO formato que visitas (GaleriaFotosVisitaOT):
 * título + contador (cargadas/esperadas) + grilla de ImagesWithZoom con el
 * nombre de cada foto, o "Sin fotos cargadas". Un item sin `photoUrl` es la
 * marca de una foto eliminada por Activaciones y se pinta como pendiente.
 * `onDelete` agrega el botón "Eliminar" en cada foto (solo Activaciones).
 * (Se reutiliza en FotosVisitaTab.)
 */
export function SeccionFotos({
  titulo,
  items,
  esperadas,
  action,
  onDelete,
}: {
  titulo: string;
  items: any[];
  esperadas: number;
  action?: ReactNode;
  onDelete?: (foto: any, nombre: string) => void;
}) {
  const cargadas = items.filter((f: any) => f?.photoUrl).length;
  return (
    <Grid item xs={12}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {titulo}
        </Typography>
        <ChipConteo cargadas={cargadas} esperadas={esperadas} />
        <Box flexGrow={1} />
        {action}
      </Box>
      <Divider sx={{ mb: 1.5 }} />
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Sin fotos cargadas.
        </Typography>
      ) : (
        <Grid container spacing={1.5}>
          {items.map((f: any, i: number) => {
            const nombre = f.photoname || f.photoName || `Foto ${i + 1}`;
            if (!f.photoUrl) {
              return (
                <Grid item key={`eliminada-${f.clave || nombre}-${i}`} xs={6} sm={4} md={3}>
                  <RecuadroEliminada marca={f} nombre={nombre} />
                </Grid>
              );
            }
            return (
              <Grid item key={f.photoId || f.photoUrl || i} xs={6} sm={4} md={3}>
                <ImagesWithZoom src={f.photoUrl} alt={nombre} />
                <Typography variant="caption" fontWeight={600} display="block" sx={{ mt: 0.5 }}>
                  {nombre}
                </Typography>
                {f.fecha_hora && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {dayjs(f.fecha_hora).format("DD/MM/YYYY HH:mm")}
                  </Typography>
                )}
                {onDelete && <BotonEliminarFoto onClick={() => onDelete(f, nombre)} />}
              </Grid>
            );
          })}
        </Grid>
      )}
    </Grid>
  );
}

/**
 * Sección "Fotos de proceso": SIEMPRE muestra los dos puntos con su título
 * (Foto punto inicial / Foto punto final): cargado, pendiente o eliminado por
 * Activaciones, para que se vea claramente qué falta.
 */
function SeccionProceso({
  ordenTrabajo,
  action,
  onDelete,
}: {
  ordenTrabajo: any;
  action?: ReactNode;
  onDelete?: (foto: any, nombre: string) => void;
}) {
  const cargadas = FOTOS_PROCESO_SLOTS.filter((s) => fotoProcesoPorTipo(ordenTrabajo, s.tipo)).length;
  return (
    <Grid item xs={12}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Fotos de proceso
        </Typography>
        <ChipConteo cargadas={cargadas} esperadas={FOTOS_PROCESO_SLOTS.length} />
        <Box flexGrow={1} />
        {action}
      </Box>
      <Divider sx={{ mb: 1.5 }} />
      <Grid container spacing={1.5}>
        {FOTOS_PROCESO_SLOTS.map((slot) => {
          const foto = fotoProcesoPorTipo(ordenTrabajo, slot.tipo);
          const eliminada = eliminadaProcesoPorTipo(ordenTrabajo, slot.tipo);
          return (
            <Grid item key={slot.tipo} xs={6} sm={4} md={3}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                <Typography variant="caption" fontWeight={800}>
                  {slot.titulo}
                </Typography>
                <Chip
                  size="small"
                  label={foto ? "Cargada" : eliminada ? "Eliminada" : "Pendiente"}
                  color={foto ? "success" : eliminada ? "error" : "warning"}
                  variant={foto ? "filled" : "outlined"}
                  sx={{ height: 20, fontSize: 11 }}
                />
              </Stack>
              {foto?.photoUrl ? (
                <>
                  <ImagesWithZoom src={foto.photoUrl} alt={slot.titulo} />
                  {foto.fecha_hora && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {dayjs(foto.fecha_hora).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                  )}
                  {onDelete && <BotonEliminarFoto onClick={() => onDelete(foto, slot.titulo)} />}
                </>
              ) : eliminada ? (
                <RecuadroEliminada marca={eliminada} />
              ) : (
                <Box
                  sx={{
                    height: 110,
                    borderRadius: 1.5,
                    border: "1px dashed",
                    borderColor: "warning.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    Sin foto · {slot.ayuda}
                  </Typography>
                </Box>
              )}
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
}

/**
 * Tab "Fotos" del flujo de traslado — mismo estilo que la galería de visitas.
 * Secciones: Ingreso (6) / Proceso (punto inicial + punto final) / Finalización
 * (6).
 *
 * Modos:
 *  - Técnico (`readOnly=false`): carga las fotos de proceso y VUELVE A SUBIR las
 *    que Activaciones haya eliminado. Nunca elimina ni reemplaza.
 *  - Lectura (`readOnly`): vistas de Activaciones/Operaciones, con resumen.
 *  - Activaciones (`readOnly` + `canDelete`): además puede ELIMINAR fotos; queda
 *    una marca y el técnico tiene que volver a subirlas.
 *
 * `live` (por defecto) sondea el estado de las fotos cada pocos segundos, así
 * lo que hace la otra parte se ve sin recargar la página. `hideProceso` se
 * mantiene solo por compatibilidad.
 */
export default function FotosTrasladoTab({
  ordenTrabajo,
  onRefresh,
  readOnly = false,
  hideProceso = false,
  canDelete = false,
  live = true,
}: {
  ordenTrabajo?: OrdenTrabajo;
  onRefresh?: () => void;
  readOnly?: boolean;
  hideProceso?: boolean;
  canDelete?: boolean;
  live?: boolean;
}) {
  const [openProceso, setOpenProceso] = useState(false);
  const [completar, setCompletar] = useState<"INGRESO" | "FINALIZACION" | null>(null);
  const [fotoAEliminar, setFotoAEliminar] = useState<FotoAEliminar | null>(null);
  const [eliminando, setEliminando] = useState(false);

  // Fotos EN VIVO superpuestas sobre la OT recibida.
  const { ot, snapshot, enVivo } = useOrdenTrabajoFotosLive(ordenTrabajo, live);
  const setFotosLive = useSetFotosTrasladoLive();
  const refrescarFotosLive = useRefrescarFotosTrasladoLive();
  // Activaciones: aviso cuando el técnico sube fotos mientras revisa.
  useAvisosFotosTraslado(snapshot, canDelete ? "activaciones" : null);

  const idOT = (ot as any)?.id_orden_trabajo as string | undefined;
  const itemsIngreso = itemsIngresoDe(ot);
  const itemsFinalizacion = [...fotosFinalizacionDe(ot), ...eliminadasFinalizacionDe(ot)];
  const resumen = resumenFotosTraslado(ot);
  const procesoCompleto = resumen.proceso >= FOTOS_PROCESO_SLOTS.length;
  const porReponer = textoFotosFaltantes(resumen);
  // Para eliminar se exige el estado en vivo: identifica la foto tal como está
  // guardada ahora mismo en la OT.
  const puedeEliminar = canDelete && enVivo && !!idOT;

  const pedirEliminar = (etapa: EtapaFotoTraslado) =>
    puedeEliminar ? (foto: any, titulo: string) => setFotoAEliminar({ etapa, foto, titulo }) : undefined;

  const confirmarEliminar = async () => {
    if (!fotoAEliminar || !idOT) return;
    setEliminando(true);
    try {
      const nuevo = await eliminarFotoTraslado(idOT, {
        etapa: fotoAEliminar.etapa,
        photoUrl: fotoAEliminar.foto?.photoUrl,
        photoId: fotoAEliminar.foto?.photoId,
      });
      setFotosLive(nuevo);
      toast.success("Foto eliminada. El técnico deberá volver a subirla.");
      onRefresh?.();
    } catch (e) {
      // El motivo ya lo mostró el cliente HTTP; se refresca por si la foto ya
      // no existía.
      console.error("Error al eliminar foto de traslado", e);
      refrescarFotosLive(idOT);
    } finally {
      setEliminando(false);
      setFotoAEliminar(null);
    }
  };

  const evidenciaCompleta =
    !resumen.incompleto &&
    resumen.ingreso >= FOTOS_INGRESO_REQUERIDAS &&
    procesoCompleto &&
    (!resumen.finalizado || resumen.finalizacion >= FOTOS_FINALIZACION_REQUERIDAS);
  // Proceso: mientras el traslado está en curso se carga lo pendiente; una vez
  // finalizado solo se repone lo que Activaciones eliminó.
  const mostrarCargarProceso =
    !readOnly && !procesoCompleto && (!resumen.finalizado || resumen.procesoEliminadas.length > 0);

  return (
    <>
      <Grid container spacing={3} sx={{ p: 1 }}>
        {readOnly && (
          <Grid item xs={12}>
            <Alert severity={evidenciaCompleta ? "success" : "warning"} variant="outlined">
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" fontWeight={700}>
                  Evidencia del técnico:
                </Typography>
                <Chip size="small" label={`Ingreso ${resumen.ingreso}/${FOTOS_INGRESO_REQUERIDAS}`} />
                <Chip size="small" label={`Proceso ${resumen.proceso}/${FOTOS_PROCESO_SLOTS.length}`} />
                <Chip size="small" label={`Finalización ${resumen.finalizacion}/${FOTOS_FINALIZACION_REQUERIDAS}`} />
                {enVivo && (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    icon={<FiberManualRecordIcon sx={{ fontSize: 12 }} />}
                    label="En vivo"
                  />
                )}
              </Stack>
              {resumen.incompleto && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Pendiente de que el técnico vuelva a subir: <b>{porReponer}</b>.
                </Typography>
              )}
              {canDelete && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Si una foto no sirve puedes eliminarla: el técnico la verá como faltante y deberá
                  volver a subirla. Los cambios se reflejan solos, sin recargar la página.
                </Typography>
              )}
            </Alert>
          </Grid>
        )}

        {!readOnly && resumen.incompleto && (
          <Grid item xs={12}>
            <Alert severity="warning" variant="outlined">
              <Typography variant="body2" fontWeight={700}>
                Activaciones eliminó fotos de este traslado.
              </Typography>
              <Typography variant="body2">Debes volver a subir: {porReponer}.</Typography>
            </Alert>
          </Grid>
        )}

        <SeccionFotos
          titulo="Fotos de ingreso"
          items={itemsIngreso}
          esperadas={FOTOS_INGRESO_REQUERIDAS}
          onDelete={pedirEliminar("INGRESO")}
          action={
            !readOnly && resumen.faltanIngreso > 0 ? (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<AddPhotoAlternateIcon />}
                onClick={() => setCompletar("INGRESO")}
              >
                Volver a subir ({resumen.faltanIngreso})
              </Button>
            ) : undefined
          }
        />
        {!hideProceso && (
          <SeccionProceso
            ordenTrabajo={ot}
            onDelete={pedirEliminar("PROCESO")}
            action={
              mostrarCargarProceso ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddPhotoAlternateIcon />}
                  onClick={() => setOpenProceso(true)}
                  disabled={!resumen.iniciado}
                  title={!resumen.iniciado ? "Primero inicia el traslado" : undefined}
                >
                  Cargar fotos de proceso
                </Button>
              ) : undefined
            }
          />
        )}
        <SeccionFotos
          titulo="Fotos de finalización"
          items={itemsFinalizacion}
          esperadas={FOTOS_FINALIZACION_REQUERIDAS}
          onDelete={pedirEliminar("FINALIZACION")}
          action={
            !readOnly && resumen.faltanFinalizacion > 0 ? (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<AddPhotoAlternateIcon />}
                onClick={() => setCompletar("FINALIZACION")}
              >
                Volver a subir ({resumen.faltanFinalizacion})
              </Button>
            ) : undefined
          }
        />
      </Grid>

      {!readOnly && !hideProceso && (
        <SubirFotosProcesoTrasladoModal
          open={openProceso}
          onClose={() => setOpenProceso(false)}
          onSuccess={() => onRefresh?.()}
          ordenTrabajo={ot}
        />
      )}

      {!readOnly && completar && (
        <CompletarFotosTrasladoModal
          open={!!completar}
          onClose={() => setCompletar(null)}
          onSuccess={() => onRefresh?.()}
          ordenTrabajo={ot}
          etapa={completar}
          faltan={completar === "INGRESO" ? resumen.faltanIngreso : resumen.faltanFinalizacion}
        />
      )}

      {/* Confirmación de borrado (solo Activaciones). */}
      <Dialog
        open={!!fotoAEliminar}
        onClose={eliminando ? undefined : () => setFotoAEliminar(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Eliminar foto del traslado</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Vas a eliminar <b>{fotoAEliminar?.titulo}</b>. El técnico la verá como faltante y deberá
            volver a subirla para completar la evidencia. Esta acción no se puede deshacer.
          </Typography>
          {fotoAEliminar?.foto?.photoUrl && (
            <Box
              component="img"
              src={fotoAEliminar.foto.photoUrl}
              alt={fotoAEliminar.titulo}
              sx={{
                width: "100%",
                maxHeight: 220,
                objectFit: "contain",
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFotoAEliminar(null)} disabled={eliminando} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={confirmarEliminar}
            disabled={eliminando}
            color="error"
            variant="contained"
            startIcon={<DeleteOutlineIcon />}
          >
            {eliminando ? "Eliminando…" : "Eliminar foto"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
